// The proposal-review vocabulary layer: turns config keys into the words the
// person approving actually sees on their own screens.
//
// Why this exists. The whole premise of the product is that someone with no
// technical knowledge can propose a change and someone with no technical
// knowledge can approve it. Every proposal-review surface used to render raw
// config keys — "Adds 1 field to purchase_order: po_number", chips reading
// `batch_jobs`, findings tagged `purchase_order.po_number` — which asks the
// reviewer to hold a mental map from config keys to their own app. That map is
// the exact thing they were promised they'd never need.
//
// Labels come from the config itself (`backend/appdata/config_preview.py`'s
// `_label_map`, on `preview.diff.labels`) and are the authority. This module
// owns the FALLBACK for a key the config never labelled, and it is the only
// place that fallback lives — the backend deliberately omits unlabelled keys
// rather than emitting a second guess that could disagree with this one.
//
// Plain functions and constant maps only (no components), same Fast-Refresh
// reason as shared/timeAgo.js and shared/proposalImpact.js.

import { FIELD_TYPES } from '../core/constants';

// Field types by the name the builder's own type picker shows ("Text", not
// "string"; "Checkbox", not "boolean"). Derived from `FIELD_TYPES` rather than
// mirrored into a second map here, so a new field type can't show up labelled
// in the editor and raw in the review of the change that added it.
const FIELD_TYPE_LABELS = Object.fromEntries(FIELD_TYPES.map((t) => [t.id, t.label]));

export function fieldTypeLabel(type) {
  return FIELD_TYPE_LABELS[type] || humanize(type);
}

// Tokens that read wrong sentence-cased. Deliberately tiny and only the ones
// that show up constantly in real configs — this is a fallback for a key the
// config author didn't label, not an attempt at a general acronym dictionary.
const ACRONYMS = new Set([
  'id', 'ids', 'url', 'api', 'po', 'sku', 'vat', 'gst', 'hr', 'ai', 'crm',
  'kpi', 'usd', 'eur', 'gbp', 'sla', 'ssn', 'dob', 'qty', 'ytd', 'mtd', 'pdf', 'csv',
]);

/**
 * `po_number` → "PO number", `unit_price` → "Unit price". Sentence case, not
 * title case: "Unit Price" reads like a column header in a spreadsheet
 * template, and these appear mid-sentence in the change narrative.
 */
export function humanize(key = '') {
  const words = String(key).split(/[_\s]+/).filter(Boolean);
  if (!words.length) return String(key);
  return words
    .map((w, i) => {
      if (ACRONYMS.has(w.toLowerCase())) return w.toUpperCase();
      if (i === 0) return w.charAt(0).toUpperCase() + w.slice(1);
      return w;
    })
    .join(' ');
}

// `labels` is `preview.diff.labels` — absent on previews computed before it
// existed, so every accessor treats it as optional and falls through to
// humanize() rather than rendering "undefined".
export function entityLabel(labels, key) {
  return labels?.entities?.[key] || humanize(key);
}

export function fieldLabel(labels, entityKey, fieldKey) {
  return labels?.fields?.[entityKey]?.[fieldKey] || humanize(fieldKey);
}

export function roleLabel(labels, key) {
  return labels?.roles?.[key] || humanize(key);
}

// The five CRUD ops a role/permission grant lists, in the verbs a reader
// actually thinks in. `list`/`get` collapse to one "View" — a reader has no
// reason to know the config format distinguishes "browse many" from "open
// one", and no realistic role wants exactly one of the two. Canonical: this
// used to be defined three times (`builder/AppOverview.jsx`,
// `shared/runtimeErrors.js`, and a fourth single-letter version in
// `builder/RolesSection.jsx`) and had already drifted into disagreeing on
// how many entries it had.
export const OP_LABELS = { list: 'view', get: 'view', create: 'create', update: 'edit', delete: 'delete' };

export function opLabel(op) {
  return OP_LABELS[op] || op;
}

/** "Purchase Order › PO number" — the breadcrumb form used on findings. */
export function fieldPath(labels, entityKey, fieldKey) {
  if (!fieldKey) return entityLabel(labels, entityKey);
  return `${entityLabel(labels, entityKey)} › ${fieldLabel(labels, entityKey, fieldKey)}`;
}

// Singular / plural nouns for each config section. `diffNarrative.js` used to
// carry its own copy of the singular half; both halves live here now so the
// narrative and the detail list can't drift apart.
const SECTION_NOUNS = {
  roles: ['role', 'roles'],
  workflows: ['approval workflow', 'approval workflows'],
  datasets: ['saved query', 'saved queries'],
  batch_jobs: ['bulk job', 'bulk jobs'],
  scheduled: ['scheduled trigger', 'scheduled triggers'],
};

export function sectionNoun(key, plural = false) {
  const pair = SECTION_NOUNS[key];
  if (pair) return plural ? pair[1] : pair[0];
  const base = humanize(key).toLowerCase();
  return plural && !base.endsWith('s') ? `${base}s` : base;
}

/** Column-header form: "Roles", "Approval workflows". */
export function sectionHeading(key) {
  const noun = sectionNoun(key, true);
  return noun.charAt(0).toUpperCase() + noun.slice(1);
}

// The eight top-level config keys `config_diff()` watches, in the terms a
// reviewer thinks about them. Four of these are access/approval changes whose
// raw key names ("default_role", "config_approval") give a non-technical
// approver no clue that they're the most consequential lines in the diff.
const TOP_LEVEL_LABELS = {
  name: 'App name',
  description: 'App description',
  icon: 'App icon',
  theme: 'Colours',
  imports: 'Data borrowed from other apps',
  config_approval: 'How many approvals a change needs',
  permissions: 'Who can see and do what',
  default_role: 'What people outside this app can do',
};

export function topLevelLabel(key) {
  return TOP_LEVEL_LABELS[key] || humanize(key);
}

// Field-spec property names, in reviewer terms. A delta on `hide_in_form` or
// `on_delete` is meaningless as a raw key and quite clear as a sentence.
const FIELD_PROP_LABELS = {
  label: 'Name on screen',
  type: 'Kind of value',
  required: 'Required',
  options: 'Choices',
  colors: 'Choice colours',
  default: 'Default value',
  readonly: 'Read-only',
  hide_in_form: 'Hidden on the form',
  hide_in_table: 'Hidden in tables',
  sensitive: 'Encrypted',
  confidential: 'Kept out of history, sharing and AI',
  sensitive_search: 'Searchable while encrypted',
  target: 'Links to',
  display: 'Shows',
  on_delete: 'If the linked record is deleted',
  validations: 'Rules',
  persisted: 'Saved to the table',
  description: 'Help text',
  placeholder: 'Placeholder',
  'compute.kind': 'How it is calculated',
  'compute.expr': 'Formula',
  'compute.entity': 'Counts records from',
  'compute.field': 'Adds up',
  'compute.match': 'Matched on',
  'compute.where': 'Only counting',
  'format.style': 'Number style',
  'format.currency': 'Currency',
  'format.decimals': 'Decimal places',
  // Entity-level props (entity_deltas walks the same shape)
  table: 'Database table',
  field_order: 'Field order',
  actions: 'Buttons',
  sublists: 'Sub-lists',
  row_colors: 'Row colours',
  scope: 'Which records',
  cron: 'Schedule',
  steps: 'Steps',
};

export function propLabel(prop) {
  return FIELD_PROP_LABELS[prop] || humanize(prop);
}

/**
 * One side of a delta as readable text. `kind` is the shape tag the backend
 * attached (see `backend/core/deltas.py`), so this never has to re-guess a
 * value's shape from its runtime type.
 */
export function valueText(value, kind = 'scalar') {
  if (kind === 'complex') return 'updated';
  if (kind === 'list') {
    if (!Array.isArray(value) || !value.length) return 'nothing';
    return value.join(', ');
  }
  if (value === true) return 'yes';
  if (value === false) return 'no';
  if (value === null || value === undefined || value === '') return 'empty';
  return String(value);
}

/**
 * `valueText` for a value that belongs to a KNOWN property, so the one prop
 * whose values are themselves config vocabulary gets translated too: a field's
 * `type` reads "Text → Long text", not "string → longtext". A type change is
 * exactly the kind with data consequences, so it's the last place to hand the
 * reviewer a word they'd have to look up.
 */
export function propValueText(prop, value, kind = 'scalar') {
  if (prop === 'type' && typeof value === 'string' && value) return fieldTypeLabel(value);
  return valueText(value, kind);
}

/**
 * A view's identity as a sentence: "Open POs, a kanban view of Purchase
 * Orders". Falls back gracefully for a preview with no `added_detail`, and for
 * a view with no title/entity of its own (a dashboard, say).
 *
 * View-type wording is humanized from the type key rather than read from
 * `viewRegistry.jsx`'s labels on purpose: that module imports every view
 * component's JSX, and this one stays a light pure-function leaf — the same
 * trade `diffNarrative.js` already documents for MODULE_META.
 */
export function viewSentence(detail, labels) {
  const { title, type, entity } = detail || {};
  const kind = type ? `${humanize(type).toLowerCase()} view` : 'view';
  const of = entity ? ` of ${entityLabel(labels, entity)}` : '';
  if (!title) return `a ${kind}${of}`;
  return `${title} — a ${kind}${of}`;
}
